// =============================================
// AuditActionBadge — wizualny status per button w AuditPanel.
// Stany: never / done / overwritten — kolory + tooltip "X min temu".
// =============================================

import { Check, AlertCircle, MinusCircle } from "lucide-react";

interface Props {
  /** ISO timestamp ostatniej akcji, null = nigdy */
  lastAt: string | null;
  /** Krótki opis (np. "248/248", "57/100 warn") — pokazany po dacie */
  detail?: string;
  /** Czy stan jest nadpisany przez inny krok (głównie LLM) */
  overwritten?: boolean;
  /** Compact mode — używać w toolbarze */
  size?: "sm" | "md";
}

export function AuditActionBadge({ lastAt, detail, overwritten, size = "md" }: Props) {
  if (!lastAt) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full border border-zinc-700/40 bg-zinc-800/30 text-zinc-400 px-2 py-0.5 ${size === "sm" ? "text-[10px]" : "text-[11px]"}`}
        title="Akcja nie była odpalana dla tej mapy"
      >
        <MinusCircle className="h-3 w-3" />
        nie odpalany
      </span>
    );
  }

  const relative = formatRelative(lastAt);

  if (overwritten) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 px-2 py-0.5 ${size === "sm" ? "text-[10px]" : "text-[11px]"}`}
        title={`Ostatnio: ${lastAt}\nStan został nadpisany przez inną akcję (np. Napraw mapę)`}
      >
        <AlertCircle className="h-3 w-3" />
        {relative}
        {detail && <span className="opacity-80 ml-1">· {detail}</span>}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 px-2 py-0.5 ${size === "sm" ? "text-[10px]" : "text-[11px]"}`}
      title={`Ostatnio: ${lastAt}`}
    >
      <Check className="h-3 w-3" />
      {relative}
      {detail && <span className="opacity-80 ml-1">· {detail}</span>}
    </span>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "?";
  const diff = Date.now() - then;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "teraz";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min temu`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h temu`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} d temu`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w} tyg. temu`;
  const mo = Math.floor(d / 30);
  return `${mo} mies. temu`;
}
